import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Search, Package, Plus, Hash, Percent } from 'lucide-react';

export const ItemSelector = ({ open, onOpenChange, items, onSelect, onCreateNew }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredItems = items.filter(item => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.name?.toLowerCase().includes(query) ||
      item.code?.toLowerCase().includes(query) ||
      item.hsn?.toLowerCase().includes(query)
    );
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const handleSelect = (item) => {
    setSearchQuery('');
    onSelect(item);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] p-0" data-testid="item-selector-modal">
        <SheetHeader className="p-4 border-b sticky top-0 bg-white z-10">
          <SheetTitle className="text-lg font-semibold">Select Item</SheetTitle>
          {/* Search */}
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search by name, code, or HSN..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12"
              data-testid="item-selector-search"
              autoFocus
            />
          </div>
        </SheetHeader>
        
        <ScrollArea className="h-[calc(90vh-140px)]">
          <div className="p-4 space-y-2">
            {/* Quick Create Button */}
            <Button
              variant="outline"
              className="w-full h-14 border-dashed justify-start gap-3"
              onClick={() => {
                onOpenChange(false);
                onCreateNew();
              }}
              data-testid="quick-create-item-btn"
            >
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Plus className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="font-medium">Create New Item</span>
            </Button>

            {filteredItems.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">
                  {searchQuery ? 'No items found matching your search' : 'No items available'}
                </p>
              </div>
            ) : (
              filteredItems.map(item => (
                <button
                  key={item.id}
                  className="selector-item w-full text-left rounded-lg hover:bg-slate-50 transition-colors"
                  onClick={() => handleSelect(item)}
                  data-testid={`item-option-${item.id}`}
                >
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Package className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-slate-900 truncate">{item.name}</h3>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {item.code && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 text-xs text-slate-600">
                            <Hash size={10} />
                            {item.code}
                          </span>
                        )}
                        {item.hsn && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-xs text-blue-600">
                            HSN: {item.hsn}
                          </span>
                        )}
                        {item.uom && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-purple-100 text-xs text-purple-600">
                            {item.uom}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-slate-900">{formatCurrency(item.rate)}</p>
                    <p className="text-xs text-amber-600 flex items-center justify-end gap-1">
                      <Percent size={10} />
                      GST: {item.gst_percent || 0}%
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default ItemSelector;
